// v10/src/components/OnboardingSlides.js
// v1.1 — Fix i18n: SLIDES movido a función getSlides(t) para evitar t() a nivel de módulo
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const getSlides = (t) => [
  {
    emoji: '✈️',
    color: ['#3b82f6', '#6d28d9'],
    title: 'Bienvenido a Condor360',
    body: 'Tu asistente de chequeo prevuelo inteligente. Diseñado para pilotos del Aeroclub Puerto Madryn — para que no se te escape nada antes de volar.',
    hint: 'Deslizá para continuar →',
    badge: null,
    voice: 'Bienvenido a Condor360. Tu asistente de chequeo prevuelo inteligente, diseñado para que no se te escape nada antes de volar.',
  },
  {
    emoji: '🔐',
    color: ['#6d28d9', '#4f46e5'],
    title: t('onboarding_login_title',{ns:'admin'}),
    body: 'Iniciá sesión con tu email y contraseña, con tu cuenta de Google, o con huella digital una vez que la configures. El acceso es solo para pilotos registrados por el administrador.',
    hint: 'Tu cuenta la crea el admin del aeroclub',
    badge: null,
    voice: 'Iniciá sesión con tu email y contraseña o con huella digital. Tu cuenta la crea el administrador del aeroclub.',
  },
  {
    emoji: '🛩️',
    color: ['#0891b2', '#3b82f6'],
    title: t('onboarding_aircraft_title',{ns:'admin'}),
    body: 'Antes de cada vuelo seleccionás la aeronave que vas a chequear. Cada avión tiene su propia lista de verificación configurada por el instructor.',
    hint: t('onboarding_aircraft_hint',{ns:'admin'}),
    badge: null,
    voice: 'Antes de cada vuelo seleccionás la aeronave. Cada avión tiene su propia lista de verificación configurada por el instructor.',
  },
  {
    emoji: '📋',
    color: ['#16a34a', '#0891b2'],
    title: t('onboarding_check_title',{ns:'admin'}),
    body: 'La app te guía ítem por ítem sección por sección. Tocá cada ítem para marcarlo como verificado, o simplemente decí "listo" con tu voz.',
    hint: 'Si saltás un ítem queda marcado como pendiente ⚠️',
    badge: null,
    voice: 'La app te guía ítem por ítem. Tocá cada ítem para marcarlo como verificado, o mantené presionado el micrófono y decí listo.',
  },
  {
    emoji: '🎙️',
    color: ['#7c3aed', '#a21caf'],
    title: 'Valentina, tu copiloto IA',
    body: 'Valentina lee cada ítem en voz alta para que puedas chequear sin mirar la pantalla. Decí "listo" y pasa al siguiente automáticamente. Funciona incluso sin conexión.',
    hint: t('onboarding_voice_hint',{ns:'admin'}),
    badge: 'IA',
    voice: 'Soy Valentina, tu copiloto de inteligencia artificial. Voy a leer cada ítem en voz alta para que puedas chequear sin mirar la pantalla.',
  },
  {
    emoji: '⚠️',
    color: ['#d97706', '#ef4444'],
    title: t('onboarding_observations_title',{ns:'admin'}),
    body: 'Si encontrás algo que no está del todo bien pero el avión puede volar, tocá el botón ⚠️ del ítem y dejá un comentario. El administrador recibe una notificación al instante.',
    hint: 'Las observaciones quedan en el PDF y en el historial',
    badge: null,
    voice: 'Si encontrás algo que no está del todo bien, tocá el botón de observación del ítem y dejá un comentario. El instructor recibe una notificación al instante.',
  },
  {
    emoji: '🏁',
    color: ['#16a34a', '#2563eb'],
    title: 'Al terminar',
    body: 'Al completar el chequeo podés exportar el PDF firmado, compartirlo por WhatsApp, o simplemente cerrar. Los datos se sincronizan automáticamente aunque estés sin internet.',
    hint: '¡Buen vuelo! 🚀',
    badge: null,
    voice: 'Al completar el chequeo, firmás digitalmente y exportás el PDF. Todo queda registrado en el servidor. ¡Buen vuelo!',
  },
];

const OnboardingSlides = ({ onComplete, darkMode }) => {
  const { t } = useTranslation();
  const SLIDES = getSlides(t);

  const [idx,         setIdx]         = useState(0);
  const [dir,         setDir]         = useState(1);
  const [isPlaying,   setIsPlaying]   = useState(false);
  const [isSpeaking,  setIsSpeaking]  = useState(false);
  const autoPlayRef = useRef(null);
  const utterRef    = useRef(null);

  const goTo = (next) => {
    if (next < 0 || next >= SLIDES.length) return;
    setDir(next > idx ? 1 : -1);
    setIdx(next);
  };

  const handleDragEnd = (_, info) => {
    if (info.offset.x < -50 && idx < SLIDES.length - 1) goTo(idx + 1);
    else if (info.offset.x > 50 && idx > 0) goTo(idx - 1);
  };

  // Limpiar síntesis al desmontar
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
      if (autoPlayRef.current) clearTimeout(autoPlayRef.current);
    };
  }, []);

  // Cuando cambia el slide y está en modo auto, hablar el nuevo slide
  useEffect(() => {
    if (!isPlaying) return;
    speakSlide(idx);
  // eslint-disable-next-line
  }, [idx, isPlaying]);

  const speakSlide = (slideIdx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (autoPlayRef.current) clearTimeout(autoPlayRef.current);

    const text = SLIDES[slideIdx].voice;
    const utt  = new SpeechSynthesisUtterance(text);
    utt.lang   = 'es-AR';
    utt.rate   = 0.95;
    utt.pitch  = 1.1;

    // Intentar usar voz en español
    const voices = window.speechSynthesis.getVoices();
    const esVoice = voices.find(v => v.lang.startsWith('es')) || null;
    if (esVoice) utt.voice = esVoice;

    utt.onstart = () => setIsSpeaking(true);
    utt.onend   = () => {
      setIsSpeaking(false);
      // Si no es el último slide, avanzar automáticamente después de 800ms
      if (slideIdx < SLIDES.length - 1) {
        autoPlayRef.current = setTimeout(() => {
          setDir(1);
          setIdx(prev => prev + 1);
        }, 800);
      } else {
        // Último slide — detener el modo auto
        setIsPlaying(false);
      }
    };
    utt.onerror = () => {
      setIsSpeaking(false);
      setIsPlaying(false);
    };

    utterRef.current = utt;
    window.speechSynthesis.speak(utt);
  };

  const handleTogglePlay = () => {
    if (isPlaying) {
      // Detener
      window.speechSynthesis?.cancel();
      if (autoPlayRef.current) clearTimeout(autoPlayRef.current);
      setIsPlaying(false);
      setIsSpeaking(false);
    } else {
      // Arrancar desde el slide actual
      setIsPlaying(true);
      speakSlide(idx);
    }
  };

  // Si el usuario navega manualmente mientras está en auto-play,
  // reiniciar el audio del nuevo slide
  const handleManualGoTo = (next) => {
    if (isPlaying) {
      window.speechSynthesis?.cancel();
      if (autoPlayRef.current) clearTimeout(autoPlayRef.current);
    }
    goTo(next);
  };

  const slide  = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <motion.div
        initial={{ y: 40, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0, scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        style={{
          width: '100%', maxWidth: '440px',
          borderRadius: '28px', overflow: 'hidden',
          background: darkMode ? 'rgba(10,14,26,0.99)' : 'rgba(255,255,255,0.99)',
          border: `1px solid ${slide.color[0]}33`,
          boxShadow: `0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px ${slide.color[0]}22`,
        }}
      >
        {/* Top color strip */}
        <motion.div
          animate={{ background: `linear-gradient(90deg, ${slide.color[0]}, ${slide.color[1]})` }}
          transition={{ duration: 0.4 }}
          style={{ height: '4px' }}
        />

        {/* Progress bar */}
        <div style={{ height: '2px', background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
          <motion.div
            animate={{ width: `${((idx + 1) / SLIDES.length) * 100}%` }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{ height: '100%', background: `linear-gradient(90deg, ${slide.color[0]}, ${slide.color[1]})`, opacity: 0.5 }}
          />
        </div>

        {/* Counter + botón Valentina */}
        <div style={{ padding: '14px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
            {idx + 1} / {SLIDES.length}
          </span>


        </div>

        {/* Slide content */}
        <div style={{ padding: '12px 28px 20px', overflow: 'hidden', minHeight: '260px' }}>
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              custom={dir}
              variants={{
                enter: d => ({ x: d * 50, opacity: 0 }),
                center: { x: 0, opacity: 1 },
                exit: d => ({ x: d * -50, opacity: 0 }),
              }}
              initial="enter" animate="center" exit="exit"
              transition={{ duration: 0.2, ease: 'easeOut' }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.12}
              onDragEnd={handleDragEnd}
              style={{ cursor: 'grab', userSelect: 'none' }}
            >
              {/* Emoji + badge */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '18px' }}>
                <motion.div
                  animate={isSpeaking
                    ? { y: [0, -6, 0], scale: [1, 1.05, 1] }
                    : { y: [0, -5, 0] }
                  }
                  transition={isSpeaking
                    ? { duration: 0.5, repeat: Infinity }
                    : { duration: 3, repeat: Infinity, ease: 'easeInOut' }
                  }
                  style={{
                    width: '68px', height: '68px', borderRadius: '20px', flexShrink: 0,
                    background: `linear-gradient(135deg, ${slide.color[0]}28, ${slide.color[1]}18)`,
                    border: `1.5px solid ${isSpeaking ? slide.color[0] : slide.color[0] + '33'}`,
                    boxShadow: isSpeaking ? `0 0 20px ${slide.color[0]}44` : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '34px', position: 'relative',
                    transition: 'border 0.3s, box-shadow 0.3s',
                  }}
                >
                  {slide.emoji}
                  {slide.badge && (
                    <div style={{
                      position: 'absolute', top: '-6px', right: '-6px',
                      background: `linear-gradient(135deg, ${slide.color[0]}, ${slide.color[1]})`,
                      color: 'white', fontSize: '9px', fontWeight: 900,
                      padding: '2px 6px', borderRadius: '6px', letterSpacing: '0.05em',
                    }}>
                      {slide.badge}
                    </div>
                  )}
                </motion.div>

                <div style={{ flex: 1, paddingTop: '4px' }}>
                  <h2 style={{
                    margin: '0 0 8px', fontSize: '20px', fontWeight: 900, lineHeight: 1.2,
                    background: `linear-gradient(135deg, ${slide.color[0]}, ${slide.color[1]})`,
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}>
                    {slide.title}
                  </h2>
                </div>
              </div>

              <p style={{
                margin: '0 0 14px', fontSize: '14.5px', lineHeight: 1.65,
                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(15,25,55,0.7)',
                fontWeight: 400,
              }}>
                {slide.body}
              </p>

              {/* Hint chip */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '6px 12px', borderRadius: '10px',
                background: `${slide.color[0]}14`,
                border: `1px solid ${slide.color[0]}25`,
              }}>
                <span style={{
                  fontSize: '12px', fontWeight: 700,
                  color: darkMode ? `${slide.color[0]}ee` : `${slide.color[0]}cc`,
                  letterSpacing: '0.02em',
                }}>
                  {slide.hint}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dots + navigation */}
        <div style={{
          padding: '0 28px 28px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderTop: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)',
          paddingTop: '16px',
        }}>
          {/* Dots */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {SLIDES.map((_, i) => (
              <motion.div
                key={i} onClick={() => handleManualGoTo(i)}
                animate={{
                  width: i === idx ? 20 : 6,
                  background: i === idx
                    ? slide.color[0]
                    : i < idx
                    ? `${slide.color[0]}66`
                    : darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                }}
                transition={{ duration: 0.2 }}
                style={{ height: '6px', borderRadius: '3px', cursor: 'pointer' }}
              />
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {!isLast && (
              <motion.button onClick={onComplete} whileTap={{ scale: 0.95 }}
                style={{
                  padding: '10px 14px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                  background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                  fontSize: '13px', fontWeight: 600, fontFamily: 'inherit',
                }}>
                Saltar
              </motion.button>
            )}
            <motion.button
              onClick={() => isLast ? onComplete() : handleManualGoTo(idx + 1)}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }}
              style={{
                padding: '11px 22px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                background: `linear-gradient(135deg, ${slide.color[0]}, ${slide.color[1]})`,
                color: 'white', fontSize: '14px', fontWeight: 800, fontFamily: 'inherit',
                boxShadow: `0 4px 16px ${slide.color[0]}44`,
                minWidth: '110px',
              }}
            >
              {isLast ? '¡Empezar! 🚀' : 'Siguiente →'}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default OnboardingSlides;
